import { useState, useCallback } from "react";
import DOMPurify from "dompurify";
import { FiChevronDown, FiCheck, FiLock, FiMessageSquare } from "react-icons/fi";
import { cn } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import { useMemberScores } from "../../../../hooks/usePanelScoringQueries";
import { calcWeightedScore, calcWeightedDiff, formatWeightedValue } from "../../../../utils/scoring";
import { DiffBadge } from "../../../DiffBadge";
import type { ScoringMode } from "../../../../hooks/usePanelScoringQueries";
import type { PanelMemberWithProgress } from "../../../../types/panel.type";
import type { PanelSupplierScores } from "../../../../types/scoring.type";
import { CompanyLogoDisplay } from "newui/features/Company/components/CompanyLogo";

type LeadScoreVisibility = "always" | "only_when_not_scoring" | "only_after_all_submitted";

interface MemberScoreAccordionProps {
	evalId: number;
	isLeadScoring?: boolean;
	isLeadSubmitted: boolean;
	leadScoreVisibility?: LeadScoreVisibility;
	members: PanelMemberWithProgress[];
	mode?: ScoringMode;
	rfxId: number;
	onAddClarification?: (inviteId: number) => void;
	onEnterScores?: () => void;
}

interface AccordionItemProps {
	evalId: number;
	isExpanded: boolean;
	isLeadRow: boolean;
	isLocked: boolean;
	member: PanelMemberWithProgress;
	mode?: ScoringMode;
	rfxId: number;
	onToggle: () => void;
	onAddClarification?: (inviteId: number) => void;
	onEnterScores?: () => void;
}

const SupplierScoreTable: React.FC<{ isFinal?: boolean; suppliers: PanelSupplierScores[]; onAddClarification?: (inviteId: number) => void }> = ({ suppliers, isFinal = false, onAddClarification }) => {
	if (suppliers.length === 0) {
		return (
			<p className="px-4 py-3 text-sm text-gray-500">
				No scores available.
			</p>
		);
	}

	return (
		<div className="space-y-4">
			{suppliers.map((supplier, index) => (
				<div key={supplier.invite_id} className={cn("px-4 border-t-2 border-blue-200 pt-4", index === 0 && "border-t-0")}>
					<div className="mb-2 flex items-center justify-between bg-blue-50 px-3 py-2 rounded">
						<div className="flex items-center gap-2">
							<CompanyLogoDisplay size="sm" supplierLogoUrl={supplier.supplier_logo} supplierName={supplier.supplier_name} />
							<h6 className="text-xs font-medium uppercase tracking-wider text-gray-500">
								{supplier.supplier_name}
							</h6>
						</div>
						{onAddClarification && (
							<button
								type="button"
								onClick={() => onAddClarification(supplier.invite_id)}
								className="inline-flex items-center gap-1 rounded-md bg-[#0031DD] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#0031DD]/90 transition-colors"
								data-testid={`add-clarification-${supplier.invite_id}`}
							>
								<FiMessageSquare size={12} />
								Add Clarification
							</button>
						)}
					</div>
					<table className="w-full table-fixed border-collapse text-sm">
						<colgroup>
							<col className="w-[200px]" />
							<col className="w-[60px]" />
							{isFinal && <col className="w-[60px]" />}
							{isFinal && <col className="w-[70px]" />}
							<col className="w-[60px]" />
							<col className="w-[70px]" />
							{isFinal && <col className="w-[50px]" />}
							<col />
						</colgroup>
						<thead>
							<tr className="border-b border-gray-100 text-left text-xs text-gray-400">
								<th className="py-1.5 pr-3 font-medium">Criterion</th>
								<th className="py-1.5 pr-3 font-medium text-right">Weight</th>
								{isFinal && <th className="py-1.5 pr-3 font-medium text-right">Initial</th>}
								{isFinal && <th className="py-1.5 pr-3 font-medium text-right">Weighted</th>}
								<th className="py-1.5 pr-3 font-medium text-right">{isFinal ? "Final" : "Score"}</th>
								<th className="py-1.5 pr-3 font-medium text-right">Weighted</th>
								{isFinal && <th className="py-1.5 pr-3 font-medium text-right">+/−</th>}
								<th className="py-1.5 font-medium">Comment</th>
							</tr>
						</thead>
						<tbody>
							{supplier.criteria.map((criterion) => {
								const weighted = calcWeightedScore(criterion.score, criterion.criterion_weight);
								const initialWeighted = calcWeightedScore(criterion.initial_score ?? null, criterion.criterion_weight);
								const diff = isFinal ? calcWeightedDiff(criterion.score, criterion.initial_score, criterion.criterion_weight) : null;
								return (
									<tr
										key={criterion.rfx_criteria_id}
										className="border-b border-gray-50 align-top hover:bg-blue-50"
									>
										<td className="py-1.5 pr-3 text-gray-700 break-words">
											{criterion.criterion_name}
										</td>
										<td className="py-1.5 pr-3 text-right text-gray-500 tabular-nums">
											{criterion.criterion_weight}%
										</td>
										{isFinal && (
											<td className="py-1.5 pr-3 text-right text-gray-400 tabular-nums">
												{criterion.initial_score !== null && criterion.initial_score !== undefined ? criterion.initial_score : "—"}
											</td>
										)}
										{isFinal && (
											<td className="py-1.5 pr-3 text-right text-gray-400 tabular-nums">
												{formatWeightedValue(initialWeighted)}
											</td>
										)}
										<td className={cn("py-1.5 pr-3 text-right tabular-nums", isFinal ? "font-semibold text-gray-900" : "font-medium")}>
											{criterion.score !== null ? criterion.score : "—"}
										</td>
										<td className={cn("py-1.5 pr-3 text-right tabular-nums", isFinal ? "font-semibold text-gray-900" : "font-medium text-gray-500")}>
											{formatWeightedValue(weighted)}
										</td>
										{isFinal && (
											<td className="py-1.5 pr-3 text-right tabular-nums text-xs font-semibold">
												<DiffBadge diff={diff} />
											</td>
										)}
										<td className="py-1.5 text-gray-500 text-xs">
											{criterion.comment ? (
												<div
													className="prose prose-xs max-w-none [&>p]:m-0"
													dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(criterion.comment) }}
												/>
											) : "—"}
										</td>
									</tr>
								);
							})}
						</tbody>
						<tfoot>
							{(() => {
								const initialTotal = supplier.criteria.reduce((sum, c) => {
									const w = calcWeightedScore(c.initial_score ?? null, c.criterion_weight);
									return w !== null ? sum + w : sum;
								}, 0);
								const hasInitial = supplier.criteria.some((c) => c.initial_score != null);
								const finalTotal = supplier.criteria.reduce((sum, c) => {
									const w = calcWeightedScore(c.score, c.criterion_weight);
									return w !== null ? sum + w : sum;
								}, 0);
								const hasFinal = supplier.criteria.some((c) => c.score !== null);
								const totalDiff = hasInitial && hasFinal ? finalTotal - initialTotal : null;

								return (
									<tr className="border-t border-gray-200">
										<td className="py-1.5 pr-3 text-sm font-medium text-gray-900">Total</td>
										<td className="py-1.5 pr-3" />
										{isFinal && <td className="py-1.5 pr-3" />}
										{isFinal && (
											<td className="py-1.5 pr-3 text-right text-gray-400 tabular-nums">
												{hasInitial ? formatWeightedValue(initialTotal) : "—"}
											</td>
										)}
										<td className="py-1.5 pr-3" />
										<td className="py-1.5 pr-3 text-right font-bold tabular-nums text-gray-900">
											{hasFinal ? formatWeightedValue(finalTotal) : "—"}
										</td>
										{isFinal && (
											<td className="py-1.5 pr-3 text-right tabular-nums text-xs font-semibold">
												<DiffBadge diff={totalDiff} placeholder="—" />
											</td>
										)}
										<td className="py-1.5" />
									</tr>
								);
							})()}
						</tfoot>
					</table>
				</div>
			))}
		</div>
	);
};

const AccordionItem: React.FC<AccordionItemProps> = ({
	rfxId,
	evalId,
	mode = "initial",
	member,
	isExpanded,
	isLocked,
	isLeadRow,
	onToggle,
	onEnterScores,
	onAddClarification,
}) => {
	const { data, isLoading } = useMemberScores(
		isExpanded ? rfxId : undefined,
		isExpanded ? evalId : undefined,
		isExpanded ? member.id : undefined,
		mode
	);

	const isSubmitted = mode === "final"
		? member.final_status === "submitted"
		: member.status === "submitted";

	return (
		<div
			className={cn(
				"rounded-lg border transition-colors",
				isExpanded ? "border-[#0031DD]/20 bg-white" : "border-gray-200 bg-white",
				isLocked && "opacity-60"
			)}
			data-testid={`member-accordion-${member.id}`}
		>
			<div
				className={cn(
					"flex w-full items-center justify-between px-4 py-3",
					!isLocked && isSubmitted && "cursor-pointer hover:bg-gray-50",
					"rounded-lg"
				)}
				onClick={!isLocked && isSubmitted ? onToggle : undefined}
				data-testid={`member-accordion-trigger-${member.id}`}
			>
				<div className="flex items-center gap-2">
					<span className="text-sm font-medium text-gray-900">
						{member.name || member.email}
						{member.role === "rfxLead" && (
							<span className="ml-1 text-xs font-normal text-gray-400">(you)</span>
						)}
					</span>
					{isSubmitted && (
						<span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
							<FiCheck size={10} aria-hidden="true" />
							Submitted
						</span>
					)}
					{!isSubmitted && !isLeadRow && (
						<span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
							Not submitted
						</span>
					)}
					{isLeadRow && !isSubmitted && onEnterScores && (
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onEnterScores();
							}}
							className="inline-flex items-center rounded-md bg-[#0031DD] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0031DD]/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-1"
							data-testid="enter-scores-btn"
						>
							Enter Scores
						</button>
					)}
				</div>
				<div className="flex items-center gap-2">
					{isLocked && !isLeadRow && (
						<span className="inline-flex items-center gap-1 text-xs text-gray-400">
							<FiLock size={12} aria-hidden="true" />
							Submit your scores first
						</span>
					)}
					{!isLocked && isSubmitted && (
						<FiChevronDown
							size={16}
							className={cn(
								"text-gray-400 transition-transform",
								isExpanded && "rotate-180"
							)}
							aria-hidden="true"
						/>
					)}
				</div>
			</div>

			{isExpanded && isSubmitted && (
				<div
					id={`member-scores-${member.id}`}
					role="region"
					aria-label={`Scores for ${member.name || member.email}`}
					className="border-t border-gray-100 py-3"
				>
					{isLoading ? (
						<div className="space-y-2 px-4">
							{[1, 2].map((i) => (
								<SkeletonBase key={i} startColor="lightgray" width="100%" height="32px" className="!rounded" />
							))}
						</div>
					) : (
						<SupplierScoreTable suppliers={data?.suppliers ?? []} isFinal={mode === "final"} onAddClarification={onAddClarification} />
					)}
				</div>
			)}
		</div>
	);
};

export const MemberScoreAccordion: React.FC<MemberScoreAccordionProps> = ({
	rfxId,
	evalId,
	mode = "initial",
	members,
	isLeadSubmitted,
	leadScoreVisibility = "always",
	isLeadScoring = false,
	onEnterScores,
	onAddClarification,
}) => {
	const [expandedId, setExpandedId] = useState<number | null>(null);

	const handleToggle = useCallback((memberId: number) => {
		setExpandedId((prev) => (prev === memberId ? null : memberId));
	}, []);

	const isFinal = mode === "final";
	const nonRevokedMembers = members.filter((m) => !m.is_removed && m.status !== "revoked");
	const isSubmittedCheck = (m: typeof members[number]) =>
		isFinal ? m.final_status === "submitted" : m.status === "submitted";
	const submittedMembers = nonRevokedMembers.filter(isSubmittedCheck);
	const otherMembers = nonRevokedMembers.filter((m) => !isSubmittedCheck(m));
	const sortedMembers = [...submittedMembers, ...otherMembers];

	if (sortedMembers.length === 0) {
		return null;
	}

	// Determine lock state based on visibility setting
	let isLocked = false;
	let lockMessage = "";
	if (leadScoreVisibility === "always") {
		isLocked = false;
	} else if (leadScoreVisibility === "only_when_not_scoring") {
		isLocked = isLeadScoring;
		lockMessage = "You cannot view member scores while you are participating as a scorer.";
	} else if (leadScoreVisibility === "only_after_all_submitted") {
		const allActiveSubmitted = members
			.filter((m) => !m.is_removed)
			.every((m) => m.status === "submitted" || m.status === "declined" || m.status === "revoked");
		isLocked = !allActiveSubmitted;
		lockMessage = "Submit your own scores to view individual panel member scores.";
	}

	return (
		<div data-testid="member-score-accordion">
			<h4 className="mb-3 text-sm font-medium text-gray-700">
				Member Scores
			</h4>
			{isLocked && lockMessage && (
				<div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
					<FiLock size={14} className="shrink-0 text-amber-600" aria-hidden="true" />
					<p className="text-xs text-amber-700">
						{lockMessage}
					</p>
				</div>
			)}
			<div
				className="space-y-2"
				role="group"
				aria-label="Panel member scores"
			>
				{sortedMembers.map((member) => (
					<AccordionItem
						key={member.id}
						rfxId={rfxId}
						evalId={evalId}
						mode={mode}
						member={member}
						isExpanded={expandedId === member.id}
						isLocked={isLocked}
						isLeadRow={member.role === "rfxLead"}
						onToggle={() => handleToggle(member.id)}
						onEnterScores={onEnterScores}
						onAddClarification={onAddClarification}
					/>
				))}
			</div>
		</div>
	);
};
